import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { X } from 'lucide-react';

const slideIn = keyframes`from { transform: translateX(100%); } to { transform: translateX(0); }`;
const slideOut = keyframes`from { transform: translateX(0); } to { transform: translateX(100%); }`;
const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const fadeOut = keyframes`from { opacity: 1; } to { opacity: 0; }`;

/** Abaixo do modal (1100): um modal aberto de dentro do drawer fica por cima dele. */
const DRAWER_Z_INDEX = 1050;

const Overlay = styled.div<{ $closing: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(16, 39, 53, 0.5);
  backdrop-filter: blur(2px);
  display: flex;
  justify-content: flex-end;
  z-index: ${DRAWER_Z_INDEX};
  animation: ${(p) => (p.$closing ? fadeOut : fadeIn)} 0.3s ease forwards;
`;

const Sheet = styled.div<{ $closing: boolean; $width?: string }>`
  width: ${(p) => p.$width || '760px'};
  max-width: 100vw;
  height: 100%;
  background: ${(p) => p.theme.background};
  box-shadow: -8px 0 30px rgba(16, 39, 53, 0.18);
  border-radius: ${(p) => p.theme.radiusLg} 0 0 ${(p) => p.theme.radiusLg};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${(p) => (p.$closing ? slideOut : slideIn)} 0.3s ease forwards;
`;

const Header = styled.div`
  padding: 1.1rem 1.5rem;
  border-bottom: 1px solid ${(p) => p.theme.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  background: ${(p) => p.theme.surface};

  h3 {
    margin: 0;
    font-size: 1.15rem;
    font-weight: 700;
    color: ${(p) => p.theme.textPrimary};
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Close = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: ${(p) => p.theme.textMuted};
  cursor: pointer;

  &:hover {
    background: ${(p) => p.theme.lightGray};
    color: ${(p) => p.theme.textPrimary};
  }
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
`;

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  width?: string;
  headerActions?: React.ReactNode;
  children: React.ReactNode;
}

/** Right-side sheet (drawer) for detail views, mirroring the Léo Delivery RightSheet. */
export const Drawer: React.FC<DrawerProps> = ({ isOpen, onClose, title, width, headerActions, children }) => {
  const [render, setRender] = useState(isOpen);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setRender(true);
      setClosing(false);
      document.body.style.overflow = 'hidden';
    } else if (render) {
      setClosing(true);
      const t = setTimeout(() => {
        setRender(false);
        setClosing(false);
        document.body.style.overflow = 'unset';
      }, 300);
      return () => {
        clearTimeout(t);
        document.body.style.overflow = 'unset';
      };
    }
  }, [isOpen, render]);

  if (!render) return null;

  return (
    <Overlay $closing={closing} onClick={(e: React.MouseEvent) => e.target === e.currentTarget && onClose()}>
      <Sheet $closing={closing} $width={width}>
        <Header>
          <h3>{title}</h3>
          <Actions>
            {headerActions}
            <Close type="button" onClick={onClose} aria-label="Fechar">
              <X size={20} />
            </Close>
          </Actions>
        </Header>
        <Body>{children}</Body>
      </Sheet>
    </Overlay>
  );
};

export default Drawer;
